import { defaultBlocksWithSavedGeneric, ownSavedBlocks } from "./_content-blocks.ts";
import { renderSignatureBlockHtml } from "./_signature-block.ts";

// --- App origin for in-email links (conversation page, genie page, etc.) ---
// Edge functions have no location context, so we always use the production domain.
// The browser preview also uses this — links in a preview email should point to prod anyway.
const _resolvedOrigin = "https://helpgenie.ai";

// Email assets are served from Supabase Storage (public bucket) so email clients
// can always fetch them, regardless of frontend deploy/SPA routing.
const ASSET_BASE = "https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/email-assets";

// --- Types (duplicated for edge function context, mirrors src/types/email-templates.ts) ---

interface ContentBlock {
  id: string;
  type: string;
  enabled: boolean;
  order: number;
  label?: string;
  settings?: Record<string, any>;
}

interface ConversationReportConfig {
  version?: number;
  colors?: {
    primary?: string;
    accent?: string;
    text?: string;
    background?: string;
    headerColor?: string;
    sectionBackground?: string;
  };
  fonts?: {
    headingSize?: number;
    googleFont?: "Poppins" | "Roboto" | "Inter" | "Lato" | "Open Sans";
    family?: string;
  };
  // V1 legacy
  sections?: {
    showMetrics?: boolean;
    showLeadInfo?: boolean;
    showTopics?: boolean;
    showInsights?: boolean;
    showTranscript?: boolean;
  };
  branding?: {
    logoUrl?: string;
    companyName?: string;
  };
  buttons?: {
    buttonStyle?: "rounded" | "square" | "pill";
    borderRadius?: number;
    shadowEnabled?: boolean;
    buttonText?: string;
  };
  // V2
  blocks?: ContentBlock[];
}

const googleFontMap: Record<string, string> = {
  Poppins: "'Poppins', 'Segoe UI', Arial, sans-serif",
  Roboto: "'Roboto', 'Segoe UI', Arial, sans-serif",
  Inter: "'Inter', 'Segoe UI', Arial, sans-serif",
  Lato: "'Lato', 'Segoe UI', Arial, sans-serif",
  "Open Sans": "'Open Sans', 'Segoe UI', Arial, sans-serif",
};

interface ConversationReportData {
  conversation: any;
  aiResult: any;
  topics: any[];
  insights: string[];
  transcript: any[];
  timezone?: string;
  redirect_url?: string;
  weeklyStats?: {
    totalCalls: number;
    analyzedCount: number;
    totalDurationSeconds: number;
    avgSentiment: number | null;
  };
  topCall?: {
    id: string;
    sentimentScore: number;
  } | null;
}

// --- Default block labels ---
const DEFAULT_LABELS: Record<string, string> = {
  metrics: "Key Performance Metrics",
  lead_info: "Insights",
  topics: "Main Topics",
  insights: "Key Points",
  transcript: "Conversation Transcript",
  mobile_app_promo: "Help Genie in your pocket",
  goal_funnel: "Insights Funnel",
  suggested_follow_up: "Suggested Follow-Up",
  next_steps: "Next Steps",
  top_call_comparison: "Best Call This Week",
  week_in_review: "This Week",
};

// Hex → rgba helper for tenant-brand-tracking tints across all renderers.
// Replaces hardcoded rgba(78,156,255,*) and rgba(255,77,184,*) leaks.
function brandHexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substr(0, 2), 16);
  const g = parseInt(clean.substr(2, 2), 16);
  const b = parseInt(clean.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// --- V1 → V2 migration for conversation reports ---
function resolveBlocks(config: ConversationReportConfig | null | undefined): ContentBlock[] {
  // New value-layer block types added 2026-05-23. Auto-injected for any
  // V2 config that doesn't have them yet so existing tenants get the
  // upgrade. Users disable individual blocks via Report Sections toggle.
  const VALUE_LAYER_AUTOINJECT: Array<{ type: string; afterType?: string }> = [
    { type: "goal_funnel", afterType: "lead_info" },
    { type: "next_steps", afterType: "insights" },
    { type: "top_call_comparison", afterType: "metrics" },
    { type: "suggested_follow_up", afterType: "insights" },
    { type: "week_in_review" }, // appended at end
    { type: "mobile_app_promo" }, // appended at end
    { type: "signature" }, // appended at end — depends on genie/team branding, starts disabled
  ];
  // Signature depends on genie/team branding a tenant may not have configured,
  // so — unlike the rest of the value layer — it starts OFF when backfilled.
  const VALUE_LAYER_DEFAULT_DISABLED = new Set(["signature"]);

  // V1 fallback / built-in default: section booleans converted to blocks,
  // with the value-layer blocks already in place.
  const sections = config?.sections || {};
  const base: ContentBlock[] = [
    { id: "metrics", type: "metrics", enabled: sections.showMetrics !== false, order: 0 },
    { id: "top_call_comparison", type: "top_call_comparison", enabled: true, order: 1 },
    { id: "goal_funnel", type: "goal_funnel", enabled: true, order: 2 },
    { id: "lead_info", type: "lead_info", enabled: sections.showLeadInfo !== false, order: 3 },
    { id: "topics", type: "topics", enabled: sections.showTopics !== false, order: 4 },
    { id: "insights", type: "insights", enabled: sections.showInsights !== false, order: 6 },
    { id: "next_steps", type: "next_steps", enabled: true, order: 7 },
    { id: "suggested_follow_up", type: "suggested_follow_up", enabled: true, order: 8 },
    { id: "transcript", type: "transcript", enabled: sections.showTranscript !== false, order: 9 },
    { id: "week_in_review", type: "week_in_review", enabled: true, order: 10 },
    { id: "mobile_app_promo", type: "mobile_app_promo", enabled: true, order: 11 },
    { id: "signature", type: "signature", enabled: false, order: 12 },
  ];

  const savedBlocks = ownSavedBlocks(config, base);
  if (!savedBlocks) return defaultBlocksWithSavedGeneric(config, base);

  const blocks = [...savedBlocks].sort((a, b) => a.order - b.order);
  const existing = new Set(blocks.map((b) => b.type));
  let maxOrder = blocks.length > 0 ? Math.max(...blocks.map((b) => b.order)) : -1;

  for (const inj of VALUE_LAYER_AUTOINJECT) {
    if (existing.has(inj.type)) continue;
    maxOrder += 1;
    blocks.push({ id: inj.type, type: inj.type, enabled: !VALUE_LAYER_DEFAULT_DISABLED.has(inj.type), order: maxOrder });
    existing.add(inj.type);
  }

  blocks.sort((a, b) => a.order - b.order);
  return pinSignatureLast(blocks);
}

// The signature depends on genie/team branding and reads as a sign-off — it
// must always render as the final section, regardless of where `order`
// (user-editable via drag-and-drop reorder in the Report Sections UI, or a
// stale saved config) places it. Sorting by `order` alone isn't enough to
// guarantee that, so pin it last as an explicit final step.
function pinSignatureLast(blocks: ContentBlock[]): ContentBlock[] {
  const signatureIndex = blocks.findIndex((b) => b.type === "signature");
  if (signatureIndex === -1 || signatureIndex === blocks.length - 1) return blocks;
  const withoutSignature = blocks.filter((b) => b.type !== "signature");
  return [...withoutSignature, blocks[signatureIndex]];
}

// --- Shared style context passed to all renderers ---
interface StyleContext {
  colors: {
    primary: string;
    accent: string;
    text: string;
    background: string;
    headerColor: string;
    sectionBackground: string;
  };
  fontFamily: string;
  borderRadius: number;
  metricButtonRadius: string;
  boxShadow: string;
  shadowEnabled: boolean;
}

// --- Block Renderers ---

function renderMetrics(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { aiResult } = data;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.metrics;

  const sentimentScore = aiResult?.sentiment_analysis?.overall_sentiment_score;
  const agentTalk = aiResult?.talk_metrics?.agent_talk_percentage;
  const customerTalk = aiResult?.talk_metrics?.customer_talk_percentage;
  const satisfaction = aiResult?.sentiment_analysis?.customer_satisfaction_score;

  const formatValue = (val: any, suffix = "") => {
    if (val === undefined || val === null) return "N/A";
    return typeof val === "number" ? `${val.toFixed(2)}${suffix}` : `${val}${suffix}`;
  };

  const numberColor = colors.headerColor || "#28406F";
  const cardBorder = "#E5EAF0";
  const labelColor = "#6B7280";

  const cell = (value: string, cellLabel: string) => `
    <td width="50%" style="padding: 3px;" valign="top">
      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #FFFFFF; border: 1px solid ${cardBorder}; border-radius: ${metricButtonRadius}; overflow: hidden;">
        <tr>
          <td style="height: 2px; background: ${colors.primary}; line-height: 2px; font-size: 0;">&nbsp;</td>
        </tr>
        <tr>
          <td style="padding: 10px 12px 9px 12px;" valign="top">
            <div style="color: ${numberColor}; font-size: 19px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; line-height: 1; margin: 0 0 3px 0;">${value}</div>
            <div style="color: ${labelColor}; font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.6px;">${cellLabel}</div>
          </td>
        </tr>
      </table>
    </td>
  `;

  return `
    <tr>
      <td style="padding: 8px 24px 4px 24px;">
        <h3 style="color: ${numberColor}; margin: 0 0 8px 0; font-size: 13px; font-weight: 600; font-family: ${fontFamily}; letter-spacing: 0.2px; text-transform: uppercase;">
          ${label}
        </h3>
        <table cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            ${cell(formatValue(sentimentScore), "Overall Sentiment")}
            ${cell(formatValue(agentTalk, "%"), "Agent Talk Time")}
          </tr>
          <tr>
            ${cell(formatValue(customerTalk, "%"), "Customer Talk Time")}
            ${cell(formatValue(satisfaction, "%"), "Customer Satisfaction")}
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderMobileAppPromo(block: ContentBlock, _data: ConversationReportData, styles: StyleContext): string {
  const settings = block.settings || {};
  const { colors, fontFamily, metricButtonRadius } = styles;
  // Conversation report goes to the BUSINESS customer (HG's customer reviewing how
  // their agent performed). The CTA is about sharing the app with their team and
  // their end customers.
  const heading = settings.heading || "Share Help Genie with your customers";
  const body = settings.body || "Get your team and customers on the app for instant support on every product.";
  // TODO_APP_STORE_URL / TODO_PLAY_STORE_URL — Sam to drop real store URLs.
  const appStoreUrl = settings.appStoreUrl || "https://helpgenie.ai/app";
  const playStoreUrl = settings.playStoreUrl || "https://helpgenie.ai/app";
  // Mascot is optional. Defaults to the HG mascot; rebranded tenants can override
  // via settings.mascotUrl or pass '' to hide it entirely.
  const mascotUrl = settings.mascotUrl !== undefined ? settings.mascotUrl : `${ASSET_BASE}/mascot-thumbsup.png`;
  const headerColor = colors.headerColor || colors.text || "#28406F";

  const mascotCell = mascotUrl
    ? `<td valign="middle" width="60" style="padding-right: 10px;"><img src="${mascotUrl}" width="48" alt="" style="display: block; width: 48px; height: auto;" /></td>`
    : "";

  return `
    <tr>
      <td style="padding: 6px 24px 14px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background: #FFFFFF; border: 1px solid #E5EAF0; border-radius: ${metricButtonRadius}; overflow: hidden;">
          <tr>
            <td style="height: 2px; background: ${colors.primary}; line-height: 2px; font-size: 0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding: 12px 14px;" valign="middle">
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  ${mascotCell}
                  <td valign="middle">
                    <div style="color: ${headerColor}; font-size: 14px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; margin: 0 0 2px 0; line-height: 1.2;">${heading}</div>
                    <div style="color: #6B7280; font-size: 12px; line-height: 1.4; font-family: ${fontFamily}; margin: 0 0 10px 0;">${body}</div>
                    <table cellpadding="0" cellspacing="0" border="0">
                      <tr>
                        <td style="padding-right: 8px;">
                          <a href="${appStoreUrl}" style="display: inline-block;">
                            <img src="${ASSET_BASE}/app-store-badge.svg" alt="Download on the App Store" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                          </a>
                        </td>
                        <td>
                          <a href="${playStoreUrl}" style="display: inline-block;">
                            <img src="${ASSET_BASE}/google-play-badge.png" alt="Get it on Google Play" width="120" height="36" style="display: block; width: 120px; height: 36px; border: 0;" />
                          </a>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderSignature(_block: ContentBlock, data: ConversationReportData, _styles: StyleContext): string {
  const agent = data.conversation?.agent || {};
  const html = renderSignatureBlockHtml(agent.name || "Your Genie", agent.description, agent.branding);
  if (!html) return "";
  return `<tr><td style="padding: 6px 24px 14px 24px;">${html}</td></tr>`;
}

function renderSectionCard(label: string, contentHtml: string, styles: StyleContext): string {
  const { fontFamily, borderRadius, colors } = styles;

  // Convert section background hex to rgba with transparency
  const hexToRgba = (hex: string, alpha: number): string => {
    const cleanHex = hex.replace("#", "");
    const r = parseInt(cleanHex.substr(0, 2), 16);
    const g = parseInt(cleanHex.substr(2, 2), 16);
    const b = parseInt(cleanHex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const sectionBgHex = colors.sectionBackground || "#FAFBFC";
  const sectionBgRgba = hexToRgba(sectionBgHex, 0.2);

  // Use a slightly higher opacity for header background
  const headerBgRgba = hexToRgba(sectionBgHex, 0.3);
  // If background is default, use E5EAF0 for border; otherwise use the custom color
  const borderColor = sectionBgHex === "#FAFBFC" ? "#E5EAF0" : sectionBgHex;

  return `
    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="margin-bottom: 25px; border: 1px solid ${borderColor}; background-color: ${sectionBgRgba}; border-radius: ${borderRadius}px; overflow: hidden;">
      <tr>
        <td style="background-color: ${headerBgRgba}; padding: 15px 20px; border-bottom: 1px solid ${borderColor};">
          <h3 style="color: ${colors.text}; margin: 0; font-size: 18px; font-weight: 600; font-family: ${fontFamily};">${label}</h3>
        </td>
      </tr>
      <tr>
        <td style="padding: 20px; background-color: ${sectionBgRgba};">
          <div style="font-size: 14px; line-height: 1.6;">
            ${contentHtml}
          </div>
        </td>
      </tr>
    </table>`;
}

function renderLeadInfo(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { aiResult } = data;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.lead_info;
  const leadInfo = aiResult?.lead_info || {};
  const headerColor = colors.headerColor;

  const contentHtml =
    Object.keys(leadInfo).length > 0
      ? Object.entries(leadInfo)
          .map(([key, value]) => {
            const displayValue = Array.isArray(value)
              ? value.length > 0
                ? value.join(", ")
                : "None"
              : value !== null && value !== undefined
                ? String(value)
                : "None";
            return `<div style="margin-bottom: 6px; color: ${colors.text}; font-size: 12px; font-family: ${fontFamily};"><strong style="color: ${headerColor};">${key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}:</strong> ${displayValue}</div>`;
          })
          .join("")
      : `<div style="font-size: 12px; color: #7C8B9C; font-family: ${fontFamily};">No insights extracted</div>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${contentHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderTopics(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { topics } = data;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.topics;
  const headerColor = colors.headerColor;

  const primaryHex = (colors.primary || "#4E9CFF").replace("#", "");
  const pr = parseInt(primaryHex.substr(0, 2), 16);
  const pg = parseInt(primaryHex.substr(2, 2), 16);
  const pb = parseInt(primaryHex.substr(4, 2), 16);
  const chipBg = `rgba(${pr}, ${pg}, ${pb}, 0.08)`;
  const chipBorder = `rgba(${pr}, ${pg}, ${pb}, 0.22)`;

  const contentHtml =
    topics.length > 0
      ? `<table cellpadding="0" cellspacing="5" border="0" width="100%"><tr><td>${topics
          .map(
            (topic) =>
              `<table cellpadding="0" cellspacing="0" border="0" style="display: inline-table; margin: 2px;"><tr><td style="padding: 5px 11px; background-color: ${chipBg}; color: ${colors.primary}; font-size: 12px; font-weight: 500; border: 1px solid ${chipBorder}; border-radius: 999px; text-align: center; font-family: ${fontFamily};">${topic}</td></tr></table>`,
          )
          .join(" ")}</td></tr></table>`
      : `<div style="font-size: 12px; color: #7C8B9C; font-family: ${fontFamily};">No topics detected</div>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${contentHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderInsights(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { insights } = data;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.insights;
  const headerColor = colors.headerColor;

  const contentHtml =
    insights.length > 0
      ? insights
          .map(
            (insight) =>
              `<div style="margin-bottom: 8px; color: ${colors.text}; font-size: 12px; font-family: ${fontFamily}; padding-left: 16px; position: relative; line-height: 1.5;"><span style="color: ${colors.primary}; font-weight: 700; position: absolute; left: 0;">&bull;</span>${insight}</div>`,
          )
          .join("")
      : `<div style="font-size: 12px; color: #7C8B9C; font-family: ${fontFamily};">No insights available</div>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${contentHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderTranscript(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { transcript } = data;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const label = block.label || DEFAULT_LABELS.transcript;
  const headerColor = colors.headerColor;

  const contentHtml =
    transcript.length > 0
      ? transcript
          .map((entry) => {
            const speaker = entry.role || entry.speaker || "user";
            const message = entry.message || entry.text || entry.content || "";
            return `<div style="margin-bottom: 10px; font-size: 12px; color: #7C8B9C; font-family: ${fontFamily}; line-height: 1.5;">
              <strong style="color: ${headerColor};">${speaker === "user" ? "User" : "Agent"}:</strong> ${message}
            </div>`;
          })
          .join("")
      : `<div style="font-size: 12px; color: #7C8B9C; font-family: ${fontFamily};">No transcript available</div>`;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 12px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${contentHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderCustomText(block: ContentBlock, _data: ConversationReportData, styles: StyleContext): string {
  const settings = block.settings || {};
  const title = settings.title || "";
  const body = settings.body || "";
  const alignment = settings.alignment || "left";
  if (!title && !body) return "";

  const titleHtml = title
    ? `<div style="margin-bottom: 8px; font-weight: 600; color: ${styles.colors.text};">${title}</div>`
    : "";
  const bodyHtml = body ? `<div style="color: ${styles.colors.text};">${body}</div>` : "";

  const contentHtml = `<div style="text-align: ${alignment};">${titleHtml}${bodyHtml}</div>`;
  const label = block.label || "Custom Section";

  return `<tr><td style="padding: 0 30px 0 30px;">${renderSectionCard(label, contentHtml, styles)}</td></tr>`;
}

function renderCtaButton(block: ContentBlock, _data: ConversationReportData, styles: StyleContext): string {
  const settings = block.settings || {};
  const url = settings.url || "#";
  const text = settings.text || "Click Here";
  const { colors, fontFamily, metricButtonRadius } = styles;

  return `
    <tr>
      <td style="padding: 10px 30px 25px 30px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%">
          <tr>
            <td align="center">
              <a href="${url}" style="display: inline-block; padding: 12px 28px; background-color: ${colors.primary}; color: #FFFFFF; text-decoration: none; font-weight: 600; font-size: 14px; border-radius: ${metricButtonRadius}; font-family: ${fontFamily}; box-shadow: ${styles.shadowEnabled ? `0 4px 12px ${brandHexToRgba(colors.primary, 0.3)}` : "none"};">${text}</a>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderDivider(_block: ContentBlock, _data: ConversationReportData, styles: StyleContext): string {
  return `
    <tr>
      <td style="padding: 10px 30px;">
        <div style="width: 100%; height: 1px; background-color: #E5EAF0;"></div>
      </td>
    </tr>`;
}

function renderImage(block: ContentBlock, _data: ConversationReportData, _styles: StyleContext): string {
  const settings = block.settings || {};
  const url = settings.url || "";
  const altText = settings.altText || "";
  const maxWidth = settings.maxWidth || 540;
  const alignment = settings.alignment || "center";
  if (!url) return "";

  return `
    <tr>
      <td style="padding: 10px 30px;" align="${alignment}">
        <img src="${url}" alt="${altText}" style="max-width: ${maxWidth}px; width: 100%; height: auto; display: block;" />
      </td>
    </tr>`;
}

// --- Value-layer renderers (page-parity blocks) ---
// Mirror the equivalent React components on /genie-hub/conversations/:id.
// Heuristics duplicated rather than imported because the React side is
// Vite + DOM and this side is Deno edge — keeping each self-contained.

function isCapturedValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as object).length > 0;
  return Boolean(value);
}

function formatScorePct(score: number): number {
  return Math.round(score > 1 ? score * 10 : score * 100);
}

function formatDurationShort(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function renderGoalFunnel(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { conversation } = data;
  const leadInfo = conversation?.details?.lead_info || conversation?.lead_info || data.aiResult?.lead_info || {};

  const agentGoalFields = conversation?.agent?.insights?.fields || conversation?.agent?.goal?.fields;
  const configuredFields: any[] = Array.isArray(agentGoalFields)
    ? agentGoalFields
    : Array.isArray(conversation?.details?.goals) &&
        conversation.details.goals.length > 0 &&
        typeof conversation.details.goals[0] === "object"
      ? conversation.details.goals
      : Object.keys(leadInfo).length > 0
        ? Object.keys(leadInfo).map((key) => ({
            key,
            label: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
          }))
        : [];
  if (configuredFields.length === 0) return "";

  const goalFields = [...configuredFields].sort((a, b) => Number(!!b.required) - Number(!!a.required));
  const caught = goalFields.filter((f: any) => isCapturedValue(leadInfo?.[f.key])).length;
  const total = goalFields.length;
  const pct = total > 0 ? Math.round((caught / total) * 100) : 0;
  const requiredFields = goalFields.filter((f: any) => f.required);
  const requiredCaught = requiredFields.filter((f: any) => isCapturedValue(leadInfo?.[f.key])).length;

  const label = block.label || DEFAULT_LABELS.goal_funnel;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const headerColor = colors.headerColor;

  const rows = goalFields
    .map((f: any) => {
      const captured = isCapturedValue(leadInfo?.[f.key]);
      const dot = captured
        ? `<span style="display:inline-block;width:14px;height:14px;border-radius:999px;background:${colors.primary};text-align:center;line-height:14px;color:#FFFFFF;font-size:10px;font-weight:700;">&#10003;</span>`
        : `<span style="display:inline-block;width:14px;height:14px;border-radius:999px;background:#E5EAF0;text-align:center;line-height:14px;color:#9CA3AF;font-size:10px;font-weight:700;">&#215;</span>`;
      const reqPill = f.required
        ? `<span style="font-size:9px;color:${colors.accent};font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-left:6px;">Required</span>`
        : "";
      return `
        <tr>
          <td style="padding:3px 0;color:${colors.text};font-size:12px;font-family:${fontFamily};">
            ${dot}&nbsp;&nbsp;<span style="vertical-align:middle;">${f.label || f.key}</span>${reqPill}
          </td>
        </tr>`;
    })
    .join("");

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <div style="font-size:19px;font-weight:700;color:${headerColor};font-family:'Baloo Paaji 2', ${fontFamily};line-height:1;margin-bottom:3px;">${caught} of ${total} <span style="font-size:13px;font-weight:600;color:${colors.accent};">&middot; ${pct}%</span></div>
              <div style="font-size:10px;color:#6B7280;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;">Insight fields captured on this call${
                requiredFields.length > 0 ? ` &middot; ${requiredCaught} of ${requiredFields.length} required` : ""
              }</div>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderSuggestedFollowUp(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { conversation, topics } = data;
  const leadInfo = conversation?.details?.lead_info || conversation?.lead_info || {};
  const firstName = (() => {
    const full =
      typeof leadInfo.full_name === "string"
        ? leadInfo.full_name
        : typeof leadInfo.name === "string"
          ? leadInfo.name
          : typeof leadInfo.first_name === "string"
            ? leadInfo.first_name
            : "";
    if (!full.trim()) return "there";
    return full.trim().split(/\s+/)[0];
  })();

  const primaryTopic = Array.isArray(topics) && topics.length > 0 ? String(topics[0]) : null;
  const genieName = conversation?.agent?.name || "our team";
  const summary = typeof conversation?.details?.summary === "string" ? conversation.details.summary : "";
  const summaryLine = summary
    ? `Just to recap: ${trimSentence(summary, 220)}`
    : `Wanted to follow up and see if there's anything else we can help with.`;

  const emailBody = [
    `Hi ${firstName},`,
    ``,
    `Thanks for the call with ${genieName}${primaryTopic ? ` about ${primaryTopic.toLowerCase()}` : " today"}.`,
    ``,
    summaryLine,
    ``,
    `If you've got any other questions, just reply to this email. Happy to help.`,
    ``,
    `Thanks,`,
    `The team`,
  ].join("\n");

  const label = block.label || DEFAULT_LABELS.suggested_follow_up;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const headerColor = colors.headerColor;

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 6px 0;font-size:16px;font-weight:600;font-family:${fontFamily};">${label}</h3>
              <div style="font-size:10px;color:#6B7280;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:8px;">Copy and adjust before sending</div>
              <div style="font-size:9px;color:#6B7280;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;margin-bottom:3px;">Subject</div>
              <div style="font-size:12px;font-weight:600;color:${colors.text};margin-bottom:8px;font-family:${fontFamily};">${
                primaryTopic
                  ? `Following up on your call about ${escapeHtml(primaryTopic.toLowerCase())}`
                  : "Following up on your call"
              }</div>
              <div style="font-size:9px;color:#6B7280;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;margin-bottom:3px;">Body</div>
              <pre style="font-family:${fontFamily};font-size:12px;color:${colors.text};line-height:1.45;background:#FAFBFC;border:1px solid #E5EAF0;border-radius:6px;padding:10px;margin:0;white-space:pre-wrap;">${escapeHtml(
                emailBody,
              )}</pre>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderNextStepsBlock(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const { conversation } = data;
  const conversationId = conversation?.id || null;
  const agentId = conversation?.agent?.id || conversation?.agent_id || null;
  const duration = conversation?.details?.duration ?? 0;
  const leadInfo = conversation?.details?.lead_info || data.aiResult?.lead_info || {};
  const leadInfoEmpty = !leadInfo || Object.keys(leadInfo).length === 0;
  const agentGoalFields = conversation?.agent?.insights?.fields || conversation?.agent?.goal?.fields;
  const goals =
    agentGoalFields ||
    conversation?.details?.goals ||
    (Object.keys(leadInfo).length > 0 ? Object.keys(leadInfo) : null);
  const goalsEmpty = !Array.isArray(goals) || goals.length === 0;
  const sentimentScore = conversation?.details?.analysis_result?.sentiment_analysis?.overall_sentiment_score;
  const lowSentiment = typeof sentimentScore === "number" && sentimentScore < 0.4;

  const PAGE_URL = conversationId ? `${_resolvedOrigin}/genie-hub/conversations/${conversationId}` : null;
  const GENIE_URL = agentId ? `${_resolvedOrigin}/genie-hub/my-genies/${agentId}` : null;

  type Step = { title: string; body: string; href: string };
  const steps: Step[] = [];

  if (duration > 0 && duration < 60 && GENIE_URL) {
    steps.push({
      title: "Short call",
      body: "Under a minute. Review the welcome message. Callers may be dropping early.",
      href: GENIE_URL,
    });
  }
  if (leadInfoEmpty && duration > 30 && GENIE_URL) {
    steps.push({
      title: "No lead info captured",
      body: "Add required fields to Goals so future calls capture name, email, intent.",
      href: GENIE_URL,
    });
  }
  if (goalsEmpty && GENIE_URL) {
    steps.push({
      title: "No insights tracked",
      body: "Define what success looks like for this genie. Insight progress shows up on every call.",
      href: GENIE_URL,
    });
  }
  if (lowSentiment && GENIE_URL) {
    steps.push({
      title: "Sentiment ran low",
      body: "Caller was not satisfied. Check the prompt and tone for this genie.",
      href: GENIE_URL,
    });
  }
  if (steps.length === 0 && GENIE_URL) {
    steps.push({
      title: "Get more calls like this",
      body: "Promote this genie. Embed it on your site or share the link with customers.",
      href: GENIE_URL,
    });
  }

  const top = steps.slice(0, 2);
  if (top.length === 0) return "";

  const label = block.label || DEFAULT_LABELS.next_steps;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const headerColor = colors.headerColor;

  const rows = top
    .map((s) => {
      return `
        <tr>
          <td style="padding-bottom:6px;">
            <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${brandHexToRgba(colors.primary, 0.05)};border:1px solid ${brandHexToRgba(colors.primary, 0.2)};border-radius:8px;">
              <tr>
                <td style="padding:9px 12px;">
                  <div style="font-size:13px;font-weight:700;color:${colors.text};margin-bottom:2px;font-family:${fontFamily};">${escapeHtml(
                    s.title,
                  )}</div>
                  <div style="font-size:11px;color:#6B7280;line-height:1.5;margin-bottom:7px;font-family:${fontFamily};">${escapeHtml(
                    s.body,
                  )}</div>
                  <a href="${s.href}" style="display:inline-block;padding:5px 11px;border-radius:999px;background:${colors.primary};color:#FFFFFF;text-decoration:none;font-size:10px;font-weight:600;font-family:${fontFamily};letter-spacing:0.2px;">Open the page &rarr;</a>
                </td>
              </tr>
            </table>
          </td>
        </tr>`;
    })
    .join("");

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">${rows}</table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderTopCallComparison(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  if (!data.topCall) return "";
  const { conversation } = data;
  const thisSentiment = conversation?.details?.analysis_result?.sentiment_analysis?.overall_sentiment_score;
  const isThisTheTop = data.topCall.id === conversation?.id;

  const label = block.label || DEFAULT_LABELS.top_call_comparison;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const headerColor = colors.headerColor;

  const topPct = formatScorePct(data.topCall.sentimentScore);
  const thisPct = typeof thisSentiment === "number" ? formatScorePct(thisSentiment) : null;

  let bodyHtml = "";
  if (isThisTheTop) {
    bodyHtml = `
      <div style="font-size:13px;font-weight:600;color:${colors.text};line-height:1.5;font-family:${fontFamily};">
        Sentiment ${topPct}% — top of the last 7 days. Worth pinning as a reference for the rest.
      </div>`;
  } else if (thisPct !== null) {
    bodyHtml = `
      <table cellpadding="0" cellspacing="0" border="0" width="100%">
        <tr>
          <td width="50%" style="padding-right:4px;">
            <div style="background:${brandHexToRgba(colors.accent, 0.06)};border:1px solid ${brandHexToRgba(colors.accent, 0.25)};border-radius:6px;padding:9px 11px;">
              <div style="font-size:18px;font-weight:800;color:${colors.accent};line-height:1;font-family:'Baloo Paaji 2', ${fontFamily};">${topPct}%</div>
              <div style="font-size:9px;color:#6B7280;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-top:3px;">Best this week</div>
            </div>
          </td>
          <td width="50%" style="padding-left:4px;">
            <div style="background:${brandHexToRgba(colors.primary, 0.06)};border:1px solid ${brandHexToRgba(colors.primary, 0.25)};border-radius:6px;padding:9px 11px;">
              <div style="font-size:18px;font-weight:800;color:${colors.primary};line-height:1;font-family:'Baloo Paaji 2', ${fontFamily};">${thisPct}%</div>
              <div style="font-size:9px;color:#6B7280;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-top:3px;">This call</div>
            </div>
          </td>
        </tr>
        <tr>
          <td colspan="2" style="padding-top:8px;">
            <a href="${_resolvedOrigin}/genie-hub/conversations/${data.topCall.id}" style="font-size:11px;font-weight:600;color:${colors.primary};text-decoration:none;font-family:${fontFamily};">See the top call &rarr;</a>
          </td>
        </tr>
      </table>`;
  } else {
    return "";
  }

  return `
    <tr>
      <td style="padding: 0 24px 10px 24px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:2px;background:${colors.primary};line-height:2px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 16px 10px 16px;">
              <h3 style="color:${headerColor};margin:0 0 8px 0;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              ${bodyHtml}
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function renderWeekInReview(block: ContentBlock, data: ConversationReportData, styles: StyleContext): string {
  const stats = data.weeklyStats;
  if (!stats || stats.totalCalls === 0) return "";

  const label = block.label || DEFAULT_LABELS.week_in_review;
  const { colors, fontFamily, metricButtonRadius } = styles;
  const headerColor = colors.headerColor;

  const tone = (i: number) => (i % 2 === 0 ? colors.primary : colors.accent);
  const tile = (label: string, value: string, accent: string) => `
    <td width="50%" style="padding:3px;" valign="top">
      <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:${accent}0D;border:1px solid ${accent}40;border-radius:6px;">
        <tr>
          <td style="padding:9px 11px;">
            <div style="font-size:18px;font-weight:800;color:${accent};line-height:1;font-family:'Baloo Paaji 2', ${fontFamily};">${value}</div>
            <div style="font-size:9px;color:#6B7280;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-top:3px;">${label}</div>
          </td>
        </tr>
      </table>
    </td>`;

  const avgSentimentDisplay = stats.avgSentiment !== null ? `${formatScorePct(stats.avgSentiment)}%` : "—";

  return `
    <tr>
      <td style="padding: 0 30px 20px 30px;">
        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background:#FFFFFF;border:1px solid #E5EAF0;border-radius:${metricButtonRadius};overflow:hidden;">
          <tr>
            <td style="height:3px;background:${colors.primary};line-height:3px;font-size:0;">&nbsp;</td>
          </tr>
          <tr>
            <td style="padding:12px 13px 10px 13px;">
              <h3 style="color:${headerColor};margin:0 0 6px 10px;font-size:13px;font-weight:600;font-family:${fontFamily};text-transform:uppercase;letter-spacing:0.4px;">${label}</h3>
              <table cellpadding="0" cellspacing="0" border="0" width="100%">
                <tr>
                  ${tile("Total calls", String(stats.totalCalls), tone(0))}
                  ${tile("Analysed", `${stats.analyzedCount}/${stats.totalCalls}`, tone(1))}
                </tr>
                <tr>
                  ${tile("Time handled", formatDurationShort(stats.totalDurationSeconds), tone(2))}
                  ${tile("Avg sentiment", avgSentimentDisplay, tone(3))}
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>`;
}

function trimSentence(s: string, max: number): string {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// --- Block Renderer Registry ---
const blockRenderers: Record<
  string,
  (block: ContentBlock, data: ConversationReportData, styles: StyleContext) => string
> = {
  metrics: renderMetrics,
  lead_info: renderLeadInfo,
  topics: renderTopics,
  insights: renderInsights,
  transcript: renderTranscript,
  mobile_app_promo: renderMobileAppPromo,
  custom_text: renderCustomText,
  cta_button: renderCtaButton,
  divider: renderDivider,
  image: renderImage,
  goal_funnel: renderGoalFunnel,
  suggested_follow_up: renderSuggestedFollowUp,
  next_steps: renderNextStepsBlock,
  top_call_comparison: renderTopCallComparison,
  week_in_review: renderWeekInReview,
  signature: renderSignature,
};

// --- Main Export ---

export function generateConversationReportEmailConfigurable(
  data: ConversationReportData,
  config?: ConversationReportConfig | null,
): string {
  const { conversation, timezone = "UTC" } = data;

  const defaults = {
    colors: {
      primary: "#4E9CFF",
      accent: "#FF4DB8",
      text: "#1A1A1A",
      background: "#F9FAFC",
      headerColor: undefined as string | undefined,
    },
    fonts: {
      family: "'Poppins', 'Segoe UI', Arial, sans-serif",
      headingSize: 32,
    },
    branding: {
      logoUrl: `${ASSET_BASE}/wordmark.png`,
      companyName: "Help Genie",
    },
    buttons: {
      buttonStyle: "rounded" as const,
      borderRadius: 8,
      shadowEnabled: true,
      buttonText: "View Full Report",
    },
  };

  const colors = { ...defaults.colors, ...config?.colors };
  const fonts = { ...defaults.fonts, ...config?.fonts };
  const branding = { ...defaults.branding, ...config?.branding };
  const buttons = { ...defaults.buttons, ...config?.buttons };

  const googleFontName = fonts.googleFont || "Poppins";
  const fontFamily = fonts.family || googleFontMap[googleFontName];
  const googleFontUrl = `https://fonts.googleapis.com/css2?family=${googleFontName}:wght@400;500;600;700&family=Baloo+Paaji+2:wght@700;800&display=swap`;

  const getButtonRadius = () => {
    switch (buttons.buttonStyle) {
      case "square":
        return "0px";
      case "pill":
        return "999px";
      case "rounded":
      default:
        return `${buttons.borderRadius}px`;
    }
  };

  // Header color cascade: explicit headerColor > tenant text color > HG navy.
  // Monochrome rebrands (all-black NZ Funds Advisor etc.) should not inherit
  // the HG navy default from the base template.
  const headerTextColor = colors.headerColor || colors.text || "#28406F";
  const boxShadow = buttons.shadowEnabled
    ? `0 4px 20px ${brandHexToRgba(colors.primary || defaults.colors.primary, 0.1)}`
    : "none";
  const metricButtonRadius = getButtonRadius();

  const styleContext: StyleContext = {
    colors: {
      primary: colors.primary || defaults.colors.primary,
      accent: colors.accent || defaults.colors.accent,
      text: colors.text || defaults.colors.text,
      background: colors.background || defaults.colors.background,
      headerColor: headerTextColor,
      sectionBackground: colors.sectionBackground || "#FAFBFC",
    },
    fontFamily,
    borderRadius: buttons.borderRadius || defaults.buttons.borderRadius,
    metricButtonRadius,
    boxShadow,
    shadowEnabled: buttons.shadowEnabled ?? defaults.buttons.shadowEnabled,
  };

  // Resolve blocks (V2 or V1 migration)
  const blocks = resolveBlocks(config);
  const enabledBlocks = blocks.filter((b) => b.enabled);

  // Render content blocks. Split around the signature block (always last in
  // enabledBlocks, see pinSignatureLast in resolveBlocks) so other content —
  // like the redirect button below — can be inserted before it without ever
  // pushing the signature out of the final position.
  const renderBlock = (block: ContentBlock) => {
    const renderer = blockRenderers[block.type];
    return renderer ? renderer(block, data, styleContext) : "";
  };
  const mainBlocks = enabledBlocks.filter((b) => b.type !== "signature");
  const signatureBlocks = enabledBlocks.filter((b) => b.type === "signature");
  const blocksHtml = mainBlocks.map(renderBlock).filter(Boolean).join("\n");
  const signatureHtml = signatureBlocks.map(renderBlock).filter(Boolean).join("\n");

  // The genie's own branded signature (when configured) replaces the fixed
  // Help Genie footer below rather than stacking under it.
  const hasCustomSignature = Boolean(
    enabledBlocks.some((b) => b.type === "signature") && data.conversation?.agent?.branding?.signatureStyle?.enabled,
  );

  const getDurationText = () => {
    try {
      const duration =
        conversation.details?.duration ||
        conversation.duration ||
        conversation.metadata?.duration_seconds ||
        conversation.metadata?.call_duration_secs ||
        conversation.metadata?.call_duration;
      if (duration && !isNaN(duration) && duration > 0) {
        return `${Math.floor(duration / 60)}m ${duration % 60}s`;
      }
      return "N/A";
    } catch {
      return "N/A";
    }
  };

  const getFormattedDate = () => {
    try {
      let dateValue = null;
      if (conversation.details?.conversation_date) {
        dateValue = new Date(conversation.details?.conversation_date);
      } else if (conversation.created_at) {
        dateValue = new Date(conversation.created_at);
      } else if (conversation.start_time) {
        dateValue = new Date(conversation.start_time);
      } else if (conversation.metadata?.start_time_unix_secs) {
        dateValue = new Date(conversation.metadata.start_time_unix_secs * 1000);
      }
      if (dateValue && !isNaN(dateValue.getTime())) {
        const formatter = new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${formatter.format(dateValue)} ${timezone}`;
      }
      return "N/A";
    } catch {
      return "N/A";
    }
  };

  // Deep link to the full conversation analysis page in the Help Genie app.
  // Rendered as a small gradient pill in the literal top-right of the email,
  // next to the logo. Gradient (primary → accent) tracks each tenant's brand
  // and matches the title underline + footer band, so it never reads as a
  // stock blue CTA.
  const conversationDetailUrl = conversation?.id
    ? `${_resolvedOrigin}/genie-hub/conversations/${conversation.id}`
    : null;
  const viewFullAnalysisChip = conversationDetailUrl
    ? `<a href="${conversationDetailUrl}" style="display: inline-block; padding: 9px 16px; background-color: ${styleContext.colors.accent}; background-image: ${styleContext.colors.primary}; color: #FFFFFF; text-decoration: none; font-weight: 600; font-size: 12px; border-radius: 999px; font-family: ${fontFamily}; letter-spacing: 0.2px; white-space: nowrap; line-height: 1;">View full analysis &rarr;</a>`
    : "";

  // "View Full Report" button — links to the actual conversation report page
  // (same URL as the viewFullAnalysisChip above), not agent.redirect_url:
  // that field is the genie's own post-conversation redirect (e.g. a
  // thank-you page a tenant configures for their voice/chat widget), and a
  // button labeled "View Full Report" pointing there was a mislabeled bug —
  // reported by support (a user clicked it expecting the report and landed
  // on the genie's redirect page instead). Only rendered if there's no
  // explicit cta_button block, so a tenant who wants their own redirect CTA
  // adds one instead of this default button being repurposed for it.
  const hasCtaBlock = enabledBlocks.some((b) => b.type === "cta_button");
  const redirectButtonHtml =
    conversationDetailUrl && !hasCtaBlock
      ? `
      <tr>
        <td style="padding: 10px 30px 25px 30px;">
          <table cellpadding="0" cellspacing="0" border="0" width="100%">
            <tr>
              <td align="center">
                <a href="${conversationDetailUrl}" style="display: inline-block; padding: 12px 28px; background-color: ${styleContext.colors.primary}; color: #FFFFFF; text-decoration: none; font-weight: 600; font-size: 14px; border-radius: ${metricButtonRadius}; font-family: ${fontFamily}; box-shadow: ${buttons.shadowEnabled ? `0 4px 12px ${brandHexToRgba(styleContext.colors.primary, 0.3)}` : "none"};">${buttons.buttonText}</a>
              </td>
            </tr>
          </table>
        </td>
      </tr>`
      : "";

  return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Conversation Analysis Report</title>
        <link rel="stylesheet" href="${googleFontUrl}" type="text/css">
        <style>@import url('${googleFontUrl}');</style>
        <!--[if mso]>
        <noscript>
          <xml>
            <o:OfficeDocumentSettings>
              <o:PixelsPerInch>96</o:PixelsPerInch>
            </o:OfficeDocumentSettings>
          </xml>
        </noscript>
        <![endif]-->
      </head>
      <body style="margin: 0; padding: 0; background-color: #F5F5F5; font-family: ${fontFamily}; -webkit-text-size-adjust: 100%; -ms-text-size-adjust: 100%;">

        <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #F5F5F5; margin: 0; padding: 0;">
          <tr>
            <td align="center" style="padding: 12px;">

              <table cellpadding="0" cellspacing="0" border="0" width="600" style="max-width: 600px; background-color: ${styleContext.colors.background}; box-shadow: ${boxShadow}; border-radius: ${styleContext.borderRadius}px; overflow: hidden;">

                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 20px 24px 4px 24px;">
                    <table cellpadding="0" cellspacing="0" border="0" width="100%">
                      <tr>
                        <td valign="middle">
                          <img src="${branding.logoUrl}" alt="${branding.companyName}" style="max-height: 28px; max-width: 160px; display: block;" />
                        </td>
                        <td valign="middle" align="right" style="padding-left: 12px; white-space: nowrap;">
                          ${viewFullAnalysisChip}
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td style="background-color: ${styleContext.colors.background}; padding: 10px 24px 16px 24px;">
                    <h1 style="color: ${headerTextColor}; margin: 0; font-size: ${Math.max(20, (fonts.headingSize ?? 32) - 8)}px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; line-height: 1.15; letter-spacing: -0.2px;">Conversation Analysis Report</h1>
                    <div style="width: 44px; height: 2px; background: ${styleContext.colors.primary}; margin: 8px 0 0; border-radius: 2px;"></div>
                  </td>
                </tr>

                <tr>
                  <td style="padding: 0 24px 10px 24px;">
                    <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: ${(() => {
                      const sb = styleContext.colors.sectionBackground;
                      const hex = sb.replace("#", "");
                      const r = parseInt(hex.substr(0, 2), 16);
                      const g = parseInt(hex.substr(2, 2), 16);
                      const b = parseInt(hex.substr(4, 2), 16);
                      return `rgba(${r}, ${g}, ${b}, 0.2)`;
                    })()}; border: 1px solid ${styleContext.colors.sectionBackground === "#FAFBFC" ? "#E5EAF0" : styleContext.colors.sectionBackground}; border-radius: ${styleContext.borderRadius}px;">
                      <tr>
                        <td style="padding: 14px 18px; color: ${headerTextColor};">
                          <h2 style="color: ${headerTextColor}; margin: 0 0 8px 0; font-size: 17px; font-weight: 600; font-family: ${fontFamily};">${
                            conversation.agent?.name || "Genie"
                          }</h2>
                          <table cellpadding="0" cellspacing="0" border="0" width="100%">
                            <tr>
                              <td style="width: 50%; font-size: 12px; color: ${styleContext.colors.text}; padding-right: 8px;">
                                <strong style="color: ${headerTextColor}; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;">Date</strong><br>
                                ${getFormattedDate()}
                              </td>
                              <td style="width: 50%; font-size: 12px; color: ${styleContext.colors.text}; padding-left: 8px;">
                                <strong style="color: ${headerTextColor}; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px;">Duration</strong><br>
                                ${getDurationText()}
                              </td>
                            </tr>
                          </table>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>

                ${blocksHtml}

                ${redirectButtonHtml}

                ${signatureHtml}

                ${hasCustomSignature ? "" : `
                <tr>
                  <td align="center" style="padding: 18px 24px; background: ${styleContext.colors.primary};">
                    <div style="color: #FFFFFF; font-size: 18px; font-weight: 700; font-family: 'Baloo Paaji 2', ${fontFamily}; letter-spacing: 0.2px; margin: 0 0 4px 0;">
                      ${branding.companyName}
                    </div>
                    <div style="color: rgba(255,255,255,0.96); font-size: 12px; font-weight: 500; font-family: ${fontFamily}; margin-bottom: 10px;">
                      Goodbye manuals. Hello Help Genie.
                    </div>
                    <div style="color: rgba(255,255,255,0.85); font-size: 11px; font-family: ${fontFamily};">
                      <a href="https://helpgenie.ai" style="color: #FFFFFF; text-decoration: none; font-weight: 500;">helpgenie.ai</a>
                      &nbsp;&nbsp;&middot;&nbsp;&nbsp; +64 9 886 4856
                    </div>
                  </td>
                </tr>
                `}

              </table>

            </td>
          </tr>
        </table>

      </body>
      </html>
    `;
}
